import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';

import { usersService } from 'src/app/services/services/users.service';
import { AuditEntity, GetLogsRequest, Module, OperationEntity } from '../../models';
import { transformAuditResults } from './audit-log-translation.helper';

import { BasicDateRangePickerComponent } from 'src/app/shared/basic-date-range-picker/basic-date-range-picker.component';

@Component({
    selector: 'app-audit-logs',
    templateUrl: './audit-logs.component.html',
    styleUrls: ['./audit-logs.component.scss'],
    standalone: false
})
export class AuditLogsComponent implements OnInit {

  @Input() modulesModel: Module[] | null = null;

  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild(BasicDateRangePickerComponent)
  dateRangePicker!: BasicDateRangePickerComponent;

  dataSource = new MatTableDataSource<AuditEntity>([]);
  displayedColumns: string[] = ['logTime', 'auditType', 'moduleName', 'what', 'auditBy'];

  audits: AuditEntity[] = [];
  operations: OperationEntity[] = [];

  auditFilters = {
    moduleKey: '-1',
    operationKey: '',
    userName: '',
    moduleName: '',
    changeText: '',
    fromDate: '',
    toDate: ''
  };

  // flags kept for now but no longer needed with calendar
  auditDateFromInvalid = false;
  auditDateToInvalid = false;

  errorMessage = '';
  isLoading = false;

  maxResultsInfoMessage = '';

  // NEW: track whether the message is a "warning" (red) or just info
  isMaxResultsWarning = false;

  // optional: constants for clarity
  private readonly MAX_DISPLAY_ROWS = 200;
  private readonly MAX_FETCH_ROWS = 201; // backend should limit to this

  // optional: min date for calendar (everything before disabled)
  minAuditDate: Date | null = new Date(new Date().setMonth(new Date().getMonth() - 3));

  constructor(
    private usersService: usersService,
    private i18n: I18NextPipe
  ) { }

  ngOnInit(): void {

    if (this.modulesModel && this.modulesModel.length > 0) {
      this.modulesModel = this.modulesModel.map(m => ({
        ...m,
        moduleName: this.i18n.transform('audit:lang_' + m.moduleName, {
          defaultValue: m.moduleName
        })
      }));
    }

    this.dataSource.sort = this.sort;
    // initial load (backend will apply its own default if from/to are empty)
    // this.loadLogs();
  }

  // ---------- API CALL ----------

  private loadLogs(): void {
    this.errorMessage = '';
    this.maxResultsInfoMessage = '';
    this.isMaxResultsWarning = false;   // reset state
    this.isLoading = true;

    const payload: GetLogsRequest = {
      auditType: this.auditFilters.moduleKey,
      operation: this.auditFilters.operationKey ?? '',
      userName: this.auditFilters.userName ?? '',
      dteFrom: this.auditFilters.fromDate ?? '',
      dteTo: this.auditFilters.toDate ?? '',
      moduleName: this.auditFilters.moduleName ?? '',
      change: this.auditFilters.changeText ?? ''
    };

    this.usersService.GetLogs(payload).subscribe({
      next: (result) => {
        const rawResults = result || [];

        const allResults = transformAuditResults(rawResults, this.i18n);

        if (allResults.length >= this.MAX_FETCH_ROWS) {
          const displayed = allResults.slice(0, this.MAX_DISPLAY_ROWS);
          this.audits = displayed;
          this.dataSource.data = displayed;

          this.isMaxResultsWarning = true;

          this.maxResultsInfoMessage = this.i18n.transform(
            'users:audit_maxResultsWarning',
            {
              defaultValue:
                'Showing 200 results. There are more matches — please refine your search to narrow down the results'
            }
          );
        } else {
          this.audits = allResults;
          this.dataSource.data = allResults;

          const count = allResults.length;

          // Show "X records found" (not red)
          this.maxResultsInfoMessage = this.i18n.transform(
            'users:audit_recordsFound',
            {
              // you can localize this key in i18next; default fallback:
              defaultValue: `${count} records found`,
              count
            }
          );

          this.isMaxResultsWarning = false;
        }

        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = this.i18n.transform(
          'users:logs_load_error',
          { defaultValue: 'Failed to load logs.' }
        );
      }
    });
  }

  onReset(): void {
    this.auditFilters = {
      moduleKey: '-1',
      operationKey: '',
      userName: '',
      moduleName: '',
      changeText: '',
      fromDate: '',
      toDate: ''
    };

    this.operations = [];
    this.maxResultsInfoMessage = '';
    this.isMaxResultsWarning = false;

    if (this.dateRangePicker) {
      this.dateRangePicker.resetToInitialPreset();
    }

    this.loadLogs();
  }

  // ---------- MODULE CHANGE ----------

  onModuleChanged(): void {
    if (this.auditFilters.moduleKey === '-1') {
      this.auditFilters.operationKey = '';
      this.auditFilters.moduleName = '';
      this.auditFilters.changeText = '';
      this.operations = [];
      return;
    }

    this.auditFilters.operationKey = '';
    this.operations = [];
    this.errorMessage = '';

    this.usersService.GetAuditOperationList(this.auditFilters.moduleKey).subscribe({
      next: (ops) => {
        this.operations = ops || [];

        if (this.operations.length > 1) {
          const selectOperationOption: OperationEntity = {
            operationKey: '',
            operationName: this.i18n.transform(
              'audit:selectOperation',
              { defaultValue: 'Select operation' }
            )
          } as OperationEntity;

          this.operations = [selectOperationOption, ...this.operations];
        }
      },
      error: () => {
        this.operations = [];
        this.errorMessage = this.i18n.transform(
          'users:logs_operation_load_error',
          { defaultValue: 'Failed to load operations.' }
        );
      }
    });
  }

  // ---------- CALENDAR RANGE CHANGE ----------

  onCalendarRangeChange(range: { dteFrom: Date; dteTo: Date }): void {
    this.auditFilters.fromDate = this.formatDateTime(range.dteFrom);
    this.auditFilters.toDate = this.formatDateTime(range.dteTo);
  }

  // ---------- SEARCH BUTTON ----------

  onSearchAudit(): void {
    this.errorMessage = '';
    this.maxResultsInfoMessage = '';
    this.isMaxResultsWarning = false;
    this.loadLogs();
  }

  // ---------- DATE HELPERS ----------

  private formatDateTime(d: Date): string {
    const dd = ('0' + d.getDate()).slice(-2);
    const mm = ('0' + (d.getMonth() + 1)).slice(-2);
    const yyyy = d.getFullYear();
    const hh = ('0' + d.getHours()).slice(-2);
    const min = ('0' + d.getMinutes()).slice(-2);
    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
  }

  private validateDateTimeFormat(value: string): boolean {
    if (!value) { return false; }
    if (value.length !== 16) { return false; }
    if (value[2] !== '-' || value[5] !== '-' || value[10] !== ' ' || value[13] !== ':') {
      return false;
    }
    return true;
  }
}
